import React, { useState } from "react";
import { UserX, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { StaffPicker, useStaffOptions } from "./StaffPicker";

const FIELD_OPTS = { manager: "pms", onlineManager: "pms", designer: "architects", drafter: "drafters", sales: "sales", "co:manager": "pms", "co:onlineManager": "pms", "co:designer": "architects" };
const ROLE_EN = { "场地PM": "Field PM", "线上PM": "Online PM", "建筑设计": "Architect", "施工图": "Drafter", "销售": "Sales", "协办·场地PM": "Co-assist · Field PM", "协办·线上PM": "Co-assist · Online PM", "协办·建筑设计": "Co-assist · Architect" };

function HitRow({ item, hit, lang, onDone }) {
  const { api } = useApp();
  const staff = useStaffOptions();
  const zh = lang === "zh";
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const save = async (name) => {
    if (!name) return;
    setBusy(true);
    try {
      const { data } = await api.post("/hrms/reassign", { pid: item.id, field: hit.field, name, prev: hit.name });
      setDone(name);
      toast.success(zh ? `${item.address}：${hit.role} 已改派给 ${name}${data.handoverClosed ? "，离职交接项已关闭" : ""}` : `${item.address}: ${ROLE_EN[hit.role] || hit.role} reassigned to ${name}`);
      onDone(item.id, hit.field, name);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "改派失败" : "Reassign failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`reassign-hit-${item.id}-${hit.field}`}>
      <span className="inline-flex items-center gap-1 text-rose-700 font-medium min-w-[120px]">
        <UserX className="h-3 w-3" />{zh ? hit.role : (ROLE_EN[hit.role] || hit.role)}
        <span className="line-through text-rose-400">{hit.name}</span>
        {hit.leaveDate && <span className="text-[10px] text-rose-400">{zh ? "离职" : "left"} {hit.leaveDate}</span>}
      </span>
      {done ? (
        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" data-testid={`reassign-done-${item.id}-${hit.field}`}><Check className="h-3.5 w-3.5" />{done}</span>
      ) : (
        <div className="w-56 -mt-1 relative">
          <StaffPicker value="" onChange={save} options={staff[FIELD_OPTS[hit.field]] || []} lang={lang} disabled={busy}
            testid={`reassign-pick-${item.id}-${hit.field}`} placeholder={zh ? `选择新${hit.role}并保存` : `Pick new ${ROLE_EN[hit.role] || hit.role}`} />
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-rose-500 absolute right-8 top-3" />}
        </div>
      )}
    </div>
  );
}

export function ReassignBanner({ data, lang, highlight, onRefresh, onOpen, onChanged }) {
  const zh = lang === "zh";
  const [open, setOpen] = useState(highlight ? "all" : "");
  if (!data?.total) return null;
  const items = data.items || [];
  return (
    <div data-testid="reassign-banner" className={`bg-rose-50 border rounded-xl px-4 py-3 ${highlight ? "border-rose-400 ring-2 ring-rose-200" : "border-rose-200"}`}>
      <div className="flex items-center gap-3">
        <UserX className="h-5 w-5 text-rose-600 shrink-0" />
        <p className="text-sm text-rose-800 flex-1">
          {zh ? `${data.total} 个在建项目的负责人已离职 — 可直接在下方选择新负责人，一键保存。` : `${data.total} active project(s) still list a departed employee — pick a replacement below to save instantly.`}
        </p>
        <button data-testid="reassign-toggle" onClick={() => setOpen(open ? "" : "all")} className="text-xs font-semibold text-rose-700 hover:underline shrink-0">
          {open ? (zh ? "收起" : "Collapse") : (zh ? "展开改派" : "Reassign here")}
        </button>
        <button data-testid="reassign-refresh" onClick={onRefresh} className="text-xs text-rose-700 hover:underline shrink-0">{zh ? "刷新" : "Refresh"}</button>
      </div>
      {!open && (
        <div className="flex flex-wrap gap-1.5 mt-2 pl-8">
          {items.map((it) => (
            <button key={it.id} data-testid={`reassign-chip-${it.id}`} onClick={() => onOpen(it.id)} title={it.hits.map((h) => `${h.role}: ${h.name}`).join(" · ")}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-800 bg-rose-100 hover:bg-rose-200 border border-rose-300 rounded-full px-2 py-0.5 transition-colors">
              <UserX className="h-3 w-3" />{it.address || it.id}
              <span className="text-rose-500">· {it.hits.map((h) => `${h.role} ${h.name}`).join(" / ")}</span>
            </button>
          ))}
        </div>
      )}
      {open && (
        <div className="mt-3 pl-8 space-y-3" data-testid="reassign-rows">
          {items.map((it) => (
            <div key={it.id} className="rounded-lg bg-white/70 border border-rose-100 px-3 py-2 space-y-1.5" data-testid={`reassign-row-${it.id}`}>
              <button onClick={() => onOpen(it.id)} className="text-xs font-bold text-slate-800 hover:text-rose-700 hover:underline text-left">{it.address || it.id}<span className="ml-2 text-[10px] font-normal text-slate-400">{it.teamName || it.team}</span></button>
              {it.hits.map((h) => <HitRow key={h.field} item={it} hit={h} lang={lang} onDone={onChanged} />)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
